import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import Text from 'components/core/Text';
import PonderStepper from 'components/Ponder/PonderStepper';
import QuestionHeader from 'components/Ponder/QuestionHeader';
import { RatingCards } from 'components/Ponder/RatingQuestion';
import TagPicker, { toggleTag } from 'components/Ponder/TagPicker';
import { NotesInput, PrimaryAction } from 'components/Ponder/styles';
import { requestLogExternalWorkout } from 'actions/activities';
import { fetchPrograms } from 'actions/programs';
import { hideModal } from 'actions/modal';
import { RATINGS } from 'constants/ponder';
import useSessionTags from 'hooks/useSessionTags';
import { StoreType } from 'reducers/types';
import { captureEvent } from 'utils/posthog';
import { Content, Background as Overlay } from '../styles';
import {
  CloseIcon,
  ModalContainer,
  FieldWrapper,
  FieldRow,
  TextInput,
  TypeaheadWrapper,
  SuggestionsList,
  SuggestionItem,
  SuggestionHint,
  ImageRow,
  ImagePreview,
  ImageActions,
  ImageButton,
  HiddenFileInput,
  StepActions,
  StepBack,
} from './styles';

// Common activities offered in the workout type typeahead, alongside the
// user's own external programs.
const COMMON_WORKOUT_TYPES = [
  'Running',
  'Walking',
  'Hiking',
  'Cycling',
  'Swimming',
  'Rowing',
  'Weightlifting',
  'Kettlebells',
  'Climbing',
  'Bouldering',
  'Yoga',
  'Pilates',
  'Stretching',
  'Martial arts',
  'Boxing',
  'Dance',
  'Basketball',
  'Soccer',
  'Tennis',
  'Surfing',
  'Skiing',
  'Skateboarding',
];

type Suggestion = {
  label: string;
  isProgram?: boolean;
};

type FormValues = {
  workoutType: string;
  completedAt: string;
  timeLength: string;
  tagIds: number[];
  rating: number;
  ponder: string;
};

const STEP = {
  activity: 'Activity',
  tags: 'Tags',
  feel: 'How it felt',
  notes: 'Notes',
};

// The same ease scale the in-session Ponder asks, worded for any activity.
const EASE_RATING = RATINGS.find(({ type }) => type === 'ease')!;
const EASE_ORDER = EASE_RATING.options.map(({ value }) => value);

/** Picking how it felt moves on; the short pause lets the selection register. */
const PICK_ADVANCE_DELAY_MS = 250;

// Local YYYY-MM-DD for <input type="date" /> (toISOString would shift the day
// for users behind UTC)
const toDateInputValue = (date: Date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Send the picked date as local noon so it lands on the same calendar day in
// every reasonable timezone
const toCompletedAt = (dateInputValue: string) => {
  const [year, month, day] = dateInputValue.split('-').map(Number);
  return new Date(year, month - 1, day, 12).toISOString();
};

const LogExternalWorkout: FC = () => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector((state: StoreType) => state.activities.isLoggingExternal);
  const submitError = useSelector((state: StoreType) => state.activities.logExternalError);
  const programs = useSelector((state: StoreType) => state.programs.all);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageFormData, setImageFormData] = useState<FormData | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const sessionTags = useSessionTags();
  const [stepIndex, setStepIndex] = useState(0);
  const advanceTimer = useRef<number>();
  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Skip the tags step only once we know there are none to pick from.
  const steps = [
    STEP.activity,
    ...(sessionTags === null || sessionTags.length > 0 ? [STEP.tags] : []),
    STEP.feel,
    STEP.notes,
  ];
  const currentIndex = Math.min(stepIndex, steps.length - 1);
  const step = steps[currentIndex];

  const goTo = useCallback((index: number) => {
    clearTimeout(advanceTimer.current);
    setStepIndex(index);
  }, []);

  // Owned external programs feed the typeahead; the saga skips the request
  // when programs are already in the store
  useEffect(() => {
    dispatch(fetchPrograms());
  }, [dispatch]);

  useEffect(
    () => () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    },
    [imagePreview],
  );

  const allSuggestions = useMemo<Suggestion[]>(() => {
    const externalPrograms = (programs || [])
      .filter(({ isExternal }) => isExternal)
      .map(({ name }) => ({ label: name, isProgram: true }));

    const programNames = new Set(externalPrograms.map(({ label }) => label.toLowerCase()));

    return [
      ...externalPrograms,
      ...COMMON_WORKOUT_TYPES.filter((label) => !programNames.has(label.toLowerCase())).map(
        (label) => ({ label }),
      ),
    ];
  }, [programs]);

  const getSuggestions = useCallback(
    (query: string) => {
      const normalized = query.trim().toLowerCase();

      if (!normalized) {
        return allSuggestions;
      }

      return allSuggestions.filter(
        ({ label }) =>
          label.toLowerCase().includes(normalized) && label.toLowerCase() !== normalized,
      );
    },
    [allSuggestions],
  );

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(hideModal());
    },
    [dispatch],
  );

  const handleImageChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.set('file', file);

    setImageFormData(formData);
    setImagePreview((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return URL.createObjectURL(file);
    });
  }, []);

  const handleRemoveImage = useCallback(() => {
    setImageFormData(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const onSubmit = useCallback(
    (values: FormValues) => {
      captureEvent('external_workout_submitted', {
        duration_minutes: parseInt(values.timeLength, 10),
        rating: values.rating,
        tag_count: values.tagIds.length,
        has_photo: Boolean(imageFormData),
      });
      dispatch(
        requestLogExternalWorkout({
          workoutType: values.workoutType.trim(),
          timeLength: parseInt(values.timeLength, 10),
          tagIds: values.tagIds,
          rating: values.rating,
          completedAt: toCompletedAt(values.completedAt),
          ponder: values.ponder.trim() || undefined,
          file: imageFormData || undefined,
        }),
      );
    },
    [dispatch, imageFormData],
  );

  const today = toDateInputValue(new Date());

  return (
    <>
      <Overlay onClick={handleClose} />
      <Content>
        <CloseIcon onClick={handleClose} />
        <ModalContainer>
          <Formik
            initialValues={
              {
                workoutType: '',
                completedAt: today,
                timeLength: '',
                tagIds: [],
                rating: 0,
                ponder: '',
              } as FormValues
            }
            onSubmit={onSubmit}
          >
            {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
              const suggestions = getSuggestions(values.workoutType);

              const selectSuggestion = (suggestion: Suggestion) => {
                setFieldValue('workoutType', suggestion.label);
                setSuggestionsOpen(false);
                setHighlightedIndex(-1);
              };

              const handleTypeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
                if (!suggestionsOpen || !suggestions.length) {
                  return;
                }

                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setHighlightedIndex((index) => (index + 1) % suggestions.length);
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setHighlightedIndex(
                    (index) => (index - 1 + suggestions.length) % suggestions.length,
                  );
                } else if (event.key === 'Enter' && highlightedIndex >= 0) {
                  event.preventDefault();
                  selectSuggestion(suggestions[highlightedIndex]);
                } else if (event.key === 'Escape') {
                  setSuggestionsOpen(false);
                  setHighlightedIndex(-1);
                }
              };

              const minutes = parseInt(values.timeLength, 10);
              const isActivityDone =
                !!values.workoutType.trim() && !!values.completedAt && minutes > 0;
              const canSubmit = isActivityDone && !!values.rating;

              const pickRating = (value: number) => {
                setFieldValue('rating', value);
                clearTimeout(advanceTimer.current);
                advanceTimer.current = window.setTimeout(
                  () => goTo(currentIndex + 1),
                  PICK_ADVANCE_DELAY_MS,
                );
              };

              const renderStep = () => {
                switch (step) {
                  case STEP.activity:
                    return (
                      <>
                        <QuestionHeader
                          title="What did you do?"
                          instruction="A run, a class, a climbing session, anything outside your GMB programs."
                        />
                        <FieldWrapper>
                          <TypeaheadWrapper>
                            <TextInput
                              name="workoutType"
                              aria-label="Type of workout"
                              value={values.workoutType}
                              onChange={(event) => {
                                handleChange(event);
                                setSuggestionsOpen(true);
                                setHighlightedIndex(-1);
                              }}
                              onFocus={() => setSuggestionsOpen(true)}
                              onBlur={(event) => {
                                handleBlur(event);
                                setSuggestionsOpen(false);
                                setHighlightedIndex(-1);
                              }}
                              onKeyDown={handleTypeKeyDown}
                              placeholder="e.g. Running, bouldering, yoga class…"
                              maxLength={120}
                              autoComplete="off"
                              autoFocus
                            />
                            {suggestionsOpen && suggestions.length > 0 && (
                              <SuggestionsList>
                                {suggestions.map((suggestion, index) => (
                                  <SuggestionItem
                                    key={suggestion.label}
                                    type="button"
                                    highlighted={index === highlightedIndex}
                                    // preventDefault keeps focus on the input so blur
                                    // doesn't close the list before the click lands
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => selectSuggestion(suggestion)}
                                  >
                                    {suggestion.label}
                                    {suggestion.isProgram && (
                                      <SuggestionHint>Program</SuggestionHint>
                                    )}
                                  </SuggestionItem>
                                ))}
                              </SuggestionsList>
                            )}
                          </TypeaheadWrapper>
                        </FieldWrapper>
                        <FieldRow>
                          <div>
                            <Text variant="captionSmall" tag="p" transform="uppercase" mb={2}>
                              Date
                            </Text>
                            <TextInput
                              name="completedAt"
                              aria-label="Date"
                              type="date"
                              value={values.completedAt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              max={today}
                            />
                          </div>
                          <div>
                            <Text variant="captionSmall" tag="p" transform="uppercase" mb={2}>
                              Minutes
                            </Text>
                            <TextInput
                              name="timeLength"
                              aria-label="Minutes"
                              type="number"
                              inputMode="numeric"
                              value={values.timeLength}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g. 30"
                              min={1}
                              max={1440}
                            />
                          </div>
                        </FieldRow>
                      </>
                    );
                  case STEP.tags:
                    return (
                      <>
                        <QuestionHeader
                          title="Which words best describe it?"
                          instruction="Pick up to three tags. There's no wrong answers."
                        />
                        <TagPicker
                          tags={sessionTags ?? []}
                          selectedIds={values.tagIds}
                          onToggle={(tagId) =>
                            setFieldValue('tagIds', toggleTag(values.tagIds, tagId))
                          }
                        />
                      </>
                    );
                  case STEP.feel:
                    return (
                      <>
                        <QuestionHeader
                          title="How did it feel?"
                          instruction="Tap the one closest to how your body felt."
                        />
                        <RatingCards
                          rating={EASE_RATING}
                          displayOrder={EASE_ORDER}
                          value={values.rating || undefined}
                          onSelect={pickRating}
                        />
                      </>
                    );
                  default:
                    return (
                      <>
                        <QuestionHeader
                          title="Add your notes"
                          instruction="Anything worth remembering, and a photo if you like. Both optional."
                        />
                        <FieldWrapper>
                          <NotesInput
                            name="ponder"
                            aria-label="Notes"
                            minRows={5}
                            value={values.ponder}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Anything worth remembering."
                            maxLength={5000}
                          />
                        </FieldWrapper>
                        <ImageRow>
                          {imagePreview && (
                            <ImagePreview src={imagePreview} alt="Workout photo preview" />
                          )}
                          <ImageActions>
                            <ImageButton
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              {imagePreview ? 'Change photo' : 'Add a photo'}
                            </ImageButton>
                            {imagePreview && (
                              <ImageButton type="button" onClick={handleRemoveImage}>
                                Remove photo
                              </ImageButton>
                            )}
                          </ImageActions>
                          <HiddenFileInput
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </ImageRow>
                        {submitError && (
                          <Text variant="textSmall" tag="p" color="ruby" mt={4}>
                            {submitError}
                          </Text>
                        )}
                      </>
                    );
                }
              };

              const isLastStep = step === STEP.notes;
              const canContinue =
                step === STEP.activity
                  ? isActivityDone
                  : step === STEP.feel
                    ? !!values.rating
                    : true;

              return (
                <>
                  <Text variant="sectionHeader" tag="h1" color="black" mb={4}>
                    LOG A WORKOUT
                  </Text>
                  <PonderStepper steps={steps} current={currentIndex} />
                  {renderStep()}
                  <StepActions>
                    {currentIndex > 0 && (
                      <StepBack onClick={() => goTo(currentIndex - 1)}>Back</StepBack>
                    )}
                    <PrimaryAction
                      isLoading={isLastStep && isSubmitting}
                      disabled={isLastStep ? !canSubmit : !canContinue}
                      handleClick={isLastStep ? handleSubmit : () => goTo(currentIndex + 1)}
                    >
                      {isLastStep ? 'Save' : 'Continue'}
                    </PrimaryAction>
                  </StepActions>
                </>
              );
            }}
          </Formik>
        </ModalContainer>
      </Content>
    </>
  );
};

export default LogExternalWorkout;
